import React, {useEffect} from 'react'
import AioText from './aio-inputs/AioText'
import {loadGoogleMapsApi} from '../util/load-script'
import {useCallbackRef} from '../hooks/useCallbackRef'
import {useNullRef} from '../hooks/useNullRef'
import usePromise from '../hooks/usePromise'

export type PlaceChangedEvent = {
    streetNumber: string;
    streetName: string;
    city: string;
    postalCode: string;
    province: string;
    country: string;
    countryShort: string;
    placeName: string;
    streetAddress: string;
    value: string;
};

type Props = Override<
    React.ComponentPropsWithoutRef<typeof AioText>,
    {
        onPlaceChanged: (ev: PlaceChangedEvent) => void;
        googleMapsApiKey: string;
        label?: string;
        testId?: string;
        required?: boolean;
    }
>;

export default function AddressAutocomplete({
    onPlaceChanged,
    googleMapsApiKey,
    label,
    ...attrs
}: Props) {
    const textInput = useNullRef<HTMLInputElement>()
    const placeChangedRef = useCallbackRef(onPlaceChanged)

    const gmapsPromise = usePromise(() => loadGoogleMapsApi(googleMapsApiKey), [googleMapsApiKey])
    const gmaps = gmapsPromise.value
    const addressInput = textInput.current

    useEffect(() => {
        if(!gmaps || !addressInput) return

        // https://developers.google.com/maps/documentation/javascript/places-autocomplete#session_tokens
        // > If your app is using the Autocomplete Widget you don't need to implement sessions, as the widget
        // handles sessions automatically in the background.
        const autocomplete = new gmaps.places.Autocomplete(addressInput, {})// {types: ['address']}

        // each field is billed; need 'geometry' for lat/lng: https://stackoverflow.com/a/57837120/65387
        autocomplete.setFields(['address_component', 'name'])

        if(navigator.geolocation) {
            // TODO: replace with company's address
            navigator.geolocation.getCurrentPosition((position) => {
                const geolocation = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                }
                const circle = new gmaps.Circle({
                    center: geolocation,
                    radius: position.coords.accuracy,
                })
                const bounds = circle.getBounds()!
                try {
                    autocomplete.setBounds(bounds)
                } catch(err) {
                    console.warn(err)
                }
            })
        }

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace()
            if(place.address_components?.length) {
                const streetNumber = place.address_components.find(c =>
                    c.types.includes('street_number')
                )?.short_name ?? ''

                const streetName = place.address_components.find(c =>
                    c.types.includes('route')
                )?.short_name ?? ''

                const city =
                    place.address_components.find(c =>
                        c.types.includes('locality')
                    )?.long_name ||
                    place.address_components.find(c =>
                        // needed for query "JFK" -> "Queens"
                        c.types.includes('sublocality_level_1')
                    )?.long_name ||
                    place.address_components.find(c =>
                        // needed for query "Milan airport"; https://kymark.zendesk.com/agent/tickets/1621748
                        c.types.includes('administrative_area_level_3')
                    )?.long_name ||
                    ''

                const postalCode = place.address_components.find(c =>
                    c.types.includes('postal_code')
                )?.short_name ?? ''

                const province = place.address_components.find(c =>
                    c.types.includes('administrative_area_level_1')
                )?.short_name ?? ''


                let countryLong = ''
                let countryShort = ''
                const countryObj = place.address_components.find((a) =>
                    a.types.includes('country'),
                )
                if(countryObj) {
                    countryLong = countryObj.long_name
                    countryShort = countryObj.short_name
                }

                const streetAddress = `${streetNumber} ${streetName}`
                let placeName = place.name ?? ''
                if(
                    streetNumber &&
                    placeName &&
                    (placeName === streetNumber ||
                        placeName.startsWith(streetNumber + ' '))
                ) {
                    placeName = ''
                }

                placeChangedRef.current({
                    streetNumber,
                    streetName,
                    city,
                    postalCode,
                    province,
                    country: countryLong,
                    countryShort,
                    placeName,
                    streetAddress,
                    value: addressInput.value,
                })
            }
        })
    }, [addressInput, gmaps, placeChangedRef])

    return (
        <AioText
            label={label ?? 'Address'}
            autoComplete="off"
            disabled={!gmapsPromise.value}
            loading={gmapsPromise.loading}
            {...attrs}
            ref={textInput}
        />
    )
}
